// src/telephony/softphoneStore.ts
//
// The softphone engine, deliberately OUTSIDE React's component tree.
//
// It used to live inside the Softphone page, which meant the registration was
// torn down the moment an agent navigated anywhere else — so they could only
// receive a call while sitting on that one screen. An agent dialer has to keep
// the line up wherever they are in the portal, so the SIP session, its audio
// element and all call state live here as a module singleton and the React
// components merely subscribe.
//
// State is exposed through useSyncExternalStore, so any number of components
// (the Softphone page, the global incoming-call overlay) render the same call.
import { UserAgent, Web } from 'sip.js';
import axiosInstance from 'api/axios.config';
import { API_ENDPOINTS } from 'api/endpoints';
import { directoryService } from 'api/services/directory.service';

export type Cfg = { ws: string; user: string; pass: string; domain: string };
export type Source = 'server' | 'manual';
export type Phase = 'offline' | 'connecting' | 'registered' | 'ringing' | 'calling' | 'in-call';
export type ProfileState = 'idle' | 'loading' | 'found' | 'none' | 'error';

export const DEFAULT_CFG: Cfg = {
  ws: 'ws://localhost:18088/ws', user: 'webphone', pass: 'webphone123', domain: '127.0.0.1',
};

export type Preset = { id: string; label: string; hint: string; source: Source; cfg?: Partial<Cfg> };

export const PRESETS: Preset[] = [
  {
    id: 'server',
    label: 'Server-provisioned',
    hint: 'Backend issues a per-admin SIP credential and tells the phone where to register.',
    source: 'server',
  },
  {
    id: 'sbc-gambia',
    label: 'Gambia SBC (WSS)',
    hint: 'Browser → nginx (TLS) → Kamailio → rtpengine → Asterisk. Dial 1000 for the test prompt.',
    source: 'manual',
    // Password deliberately absent — it is not committed, and it is no longer
    // persisted either: paste it each session. A diagnostic credential is still
    // a working SIP login on a real switch, so it is not written to disk.
    cfg: { ws: 'wss://sbc-gambia.teevoice.com/ws', domain: '18.199.115.205', user: 'webrtc1' },
  },
  {
    id: 'local',
    label: 'Local test Asterisk',
    hint: 'Only reachable when the portal itself is served over http from this machine.',
    source: 'manual',
    cfg: DEFAULT_CFG,
  },
];

const LS_CFG = 'teeoff.softphone.cfg';
const LS_SRC = 'teeoff.softphone.source';
const LS_HDR = 'teeoff.softphone.hdrs';
// The chosen switch survives a reload. Not a secret — a provider id the user is
// already authorised for, and the server re-checks it on every provision.
const LS_PROVIDER = 'teeoff.softphone.providerId';

export type State = {
  cfg: Cfg;
  source: Source;
  presetId: string;
  phase: Phase;
  caller: string;
  inHeaders: Record<string, string>;
  outHeaders: string;
  profile: any;
  profileState: ProfileState;
  /**
   * True when the match came from the team directory rather than the CRM — an
   * internal extension, not a customer. The UI uses this to label a colleague
   * as such and to NOT offer "save to contacts", which would create a CRM
   * record for a seat that the directory already describes and keeps current.
   */
  profileIsInternal: boolean;
  logLines: string[];
  /** True while the primary call is parked for an attended transfer. */
  onHold: boolean;
  /** The consultation leg of an attended transfer. */
  consultPhase: 'idle' | 'calling' | 'in-call';
  consultTarget: string;
  /** Conference room we are in, or joining. */
  conferenceRoom: string;
  /** Who else is in that room. Empty until the first poll returns. */
  participants: Array<{ channel: string; callerId: string }>;
  /**
   * The switches this user's ORGANIZATION may register a phone to.
   *
   * Empty until asked for. The backend refuses to guess when an organization
   * has more than one and no choice was made — so without this list the phone
   * simply never provisions, and the only symptom is a log line. That is the
   * bug this exists to prevent, not a nicety.
   */
  providers: Array<{ providerId: number; name: string; region?: string }>;
  /** True only when there is a genuine choice — one switch is a fact, not a decision. */
  switchable: boolean;
  /** The chosen switch, or null to let the server pick when it can. */
  providerId: number | null;
  /**
   * Why provisioning failed, for the user rather than the log. Empty when
   * nothing is wrong. The old code swallowed this into `logLines` and fell back
   * to saved settings, so a phone that could not be issued looked identical to
   * one nobody had configured.
   */
  provisionError: string;
};

function loadCfg(): Cfg {
  try {
    const saved = JSON.parse(localStorage.getItem(LS_CFG) || '{}');
    // Purge a password persisted by an older build, on load rather than on next
    // connect. Waiting for a connect would leave a live SIP login sitting in
    // localStorage for every existing user until they happened to reconnect —
    // and the ones least likely to reconnect are exactly the stale ones.
    if (saved && typeof saved.pass === 'string' && saved.pass !== '') {
      localStorage.setItem(LS_CFG, JSON.stringify({ ...saved, pass: '' }));
    }
    return { ...DEFAULT_CFG, ...saved };
  } catch { return DEFAULT_CFG; }
}

let state: State = {
  cfg: loadCfg(),
  source: (localStorage.getItem(LS_SRC) as Source) || 'server',
  presetId: localStorage.getItem(LS_SRC) === 'manual' ? 'sbc-gambia' : 'server',
  phase: 'offline',
  caller: '',
  inHeaders: {},
  outHeaders: localStorage.getItem(LS_HDR) || '',
  profile: null,
  profileState: 'idle',
  profileIsInternal: false,
  logLines: [],
  onHold: false,
  consultPhase: 'idle',
  consultTarget: '',
  conferenceRoom: '',
  participants: [],
  providers: [],
  switchable: false,
  providerId: (() => {
    const v = localStorage.getItem(LS_PROVIDER);
    return v ? Number(v) : null;
  })(),
  provisionError: '',
};

const subscribers = new Set<() => void>();
export function subscribe(fn: () => void): () => void {
  subscribers.add(fn);
  return () => { subscribers.delete(fn); };
}
export function getState(): State { return state; }

function set(patch: Partial<State>) {
  state = { ...state, ...patch };
  subscribers.forEach(fn => fn());
}

function log(m: string) {
  const line = `[${new Date().toLocaleTimeString()}] ${m}`;
  // eslint-disable-next-line no-console
  console.log('[softphone]', m);
  set({ logLines: [...state.logLines.slice(-200), line] });
}

// ---------------------------------------------------------------------------
// Audio + ringer. Both are created against document, not a React ref: the
// element that plays the far end has to outlive whatever page is mounted, or
// audio would cut out the moment the agent navigates.
// ---------------------------------------------------------------------------
let remoteAudio: HTMLAudioElement | null = null;
function getRemoteAudio(): HTMLAudioElement {
  if (!remoteAudio) {
    remoteAudio = document.createElement('audio');
    remoteAudio.autoplay = true;
    remoteAudio.style.display = 'none';
    document.body.appendChild(remoteAudio);
  }
  return remoteAudio;
}

let ring: { ctx: AudioContext; timer: number } | null = null;
function stopRing() {
  if (!ring) return;
  window.clearInterval(ring.timer);
  ring.ctx.close().catch(() => {});
  ring = null;
}
function startRing() {
  if (ring) return;
  try {
    const Ctor = window.AudioContext || (window as any).webkitAudioContext;
    const ctx = new Ctor();
    const burst = () => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = 440;
      // Ramped rather than hard-stopped, which clicks audibly.
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.9);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(); osc.stop(ctx.currentTime + 1);
    };
    burst();
    ring = { ctx, timer: window.setInterval(burst, 3000) };
  } catch { /* autoplay policy — the visual alert still shows */ }
}

// ---------------------------------------------------------------------------
// Reading the incoming INVITE.
//
// SessionManager hands the Session straight to the delegate, so this reads a
// PUBLIC getter. Under SimpleUser it had to reach into a private field, because
// that delegate took no arguments at all.
// ---------------------------------------------------------------------------
function readIncomingHeaders(session: any): Record<string, string> {
  const out: Record<string, string> = {};
  try {
    // `request` is Invitation's public getter for the incoming INVITE.
    const msg = session?.request || session?.incomingInviteRequest?.message;
    const raw = msg?.headers || {};
    // sip.js title-cases names via headerize(), so the key is "X-Campaign-Id"
    // and each value is stored as { raw: <value> }.
    Object.keys(raw).forEach(k => {
      if (!/^x-/i.test(k)) return;
      const first = Array.isArray(raw[k]) ? raw[k][0] : undefined;
      const v = typeof first === 'string' ? first : first?.raw;
      if (typeof v === 'string' && v.trim()) out[k] = v.trim();
    });
  } catch { /* no screen pop, but the call still works */ }
  return out;
}

function readCallerId(session: any): { display: string; number: string } {
  try {
    const id = session?.remoteIdentity;
    const name = id?.displayName;
    const num = id?.uri?.user || '';
    return { display: name && num ? `${name} <${num}>` : (name || num || 'Unknown'), number: num };
  } catch { return { display: 'Unknown', number: '' }; }
}

/** "X-Campaign-Id: camp-42" per line -> SIP extra headers. Blank lines ignored. */
export function parseHeaderLines(text: string): string[] {
  return text.split('\n').map(l => l.trim()).filter(l => l.includes(':'));
}

// ---------------------------------------------------------------------------
// Pre-answer hook: fired while the call is still ringing, so the agent has the
// customer on screen before they pick up.
//
// Deliberately OUT of the call path. Having Asterisk look the customer up
// before it rings the agent works for any device, but puts CRM latency into
// call setup, so a slow or broken CRM delays or fails ringing. Here a failed
// lookup costs nothing: the call rings on time and the caller shows as unknown.
// ---------------------------------------------------------------------------
async function runPreAnswerLookup(number: string) {
  if (!number) { set({ profileState: 'idle', profileIsInternal: false }); return; }
  set({ profileState: 'loading', profileIsInternal: false });
  try {
    const res = await axiosInstance.get(API_ENDPOINTS.CONTACT_PHONE(encodeURIComponent(number)));
    // This API wraps payloads in data.data and uses camelCase; tolerate both.
    const d = res.data?.data ?? res.data;
    const c = Array.isArray(d) ? d[0] : d;
    if (c && (c.name || c.firstName || c.uid || c.id)) {
      set({ profile: c, profileState: 'found', profileIsInternal: false });
      log(`caller matched: ${c.name || [c.firstName, c.lastName].filter(Boolean).join(' ') || c.uid}`);
      return;
    }
    await resolveColleagueOrUnknown(number);
  } catch (e: any) {
    // A CRM miss can arrive as a 404 rather than an empty body, and an internal
    // call would ALWAYS take that path — a bare extension can never match a CRM
    // record. So a failed contact lookup still tries the directory before the
    // caller is declared unknown; only a directory miss too is really unknown.
    if (e?.response?.status === 404) {
      await resolveColleagueOrUnknown(number);
      return;
    }
    set({ profileState: 'error', profileIsInternal: false });
    log(`lookup failed (${e?.response?.status || e?.message}) — call unaffected`);
  }
}

/**
 * Second half of the pre-answer lookup: not a customer, so is it a colleague?
 *
 * Resolved from live seats rather than from contact rows duplicated per agent,
 * so renaming or removing a seat cannot leave a stale colleague behind. Failure
 * is indistinguishable from "not a colleague" on purpose — this runs while the
 * phone is ringing and must never turn a lookup problem into a call problem.
 */
async function resolveColleagueOrUnknown(number: string) {
  const colleague = await directoryService.lookup(number).catch(() => null);
  if (colleague) {
    set({
      profile: { name: colleague.displayName, extension: colleague.extension },
      profileState: 'found',
      profileIsInternal: true,
    });
    log(`internal call from ${colleague.displayName} (${colleague.extension})`);
  } else {
    set({ profileState: 'none', profileIsInternal: false });
    log('caller not in contacts');
  }
}

// ---------------------------------------------------------------------------
// Conference roster.
//
// A room with one person and a room with three sound identical until somebody
// speaks, so the agent is told who is actually in there. Asterisk is the only
// thing that knows; it is read over the SBC's own TLS endpoint rather than
// through the portal API, because that is where Asterisk is.
//
// Polled rather than pushed: there is no event channel from Asterisk to the
// browser, and a 3s poll for the duration of a conference is cheap. A failed
// poll is silent by design — losing the roster must never look like losing the
// call.
// ---------------------------------------------------------------------------
const ROSTER_BASE = 'https://sbc-gambia.teevoice.com/conf';
let rosterTimer: number | null = null;

function stopRoster() {
  if (rosterTimer !== null) { window.clearInterval(rosterTimer); rosterTimer = null; }
  set({ participants: [] });
}

function startRoster(room: string) {
  stopRoster();
  const poll = async () => {
    try {
      // Plain fetch, not the portal's axios instance: this is a different host
      // and must not carry the portal's Authorization header.
      const r = await fetch(`${ROSTER_BASE}/${encodeURIComponent(room)}`);
      if (!r.ok) return;
      const d = await r.json();
      set({ participants: Array.isArray(d?.participants) ? d.participants : [] });
    } catch { /* roster unavailable — the call itself is unaffected */ }
  };
  poll();
  rosterTimer = window.setInterval(poll, 3000);
}

// ---------------------------------------------------------------------------
// The SIP session itself.
//
// SessionManager, not SimpleUser. SimpleUser is documented as handling exactly
// ONE concurrent session, which makes attended transfer impossible: connecting
// two parties needs a second call up at the same time so the agent can speak to
// the target first. SessionManager also passes the Session to every delegate
// method, which removes the reach into private fields that reading the incoming
// INVITE used to require.
// ---------------------------------------------------------------------------
let sm: Web.SessionManager | null = null;

/** The call the agent is actually on. */
let primary: any = null;
/** The consultation leg raised during an attended transfer. */
let consult: any = null;
/**
 * Room to join once the current leg finishes tearing down.
 *
 * Moving a caller into a conference is a blind transfer, and a blind transfer
 * by definition drops the transferor - so the agent's own leg ends as a direct
 * result of the action. Without this the agent is simply hung up on, with the
 * audio cutting out and no sign of where the call went. That is exactly how it
 * behaved when the button was first shipped.
 */
let pendingConference = '';

function clearCallContext() {
  set({ inHeaders: {}, caller: '', profile: null, profileState: 'idle', profileIsInternal: false, onHold: false });
}

export const actions = {
  /**
   * Re-run the caller lookup for the call currently on screen.
   *
   * Used after saving a contact mid-call, so the panel stops saying "not in
   * contacts" and shows the name the agent just typed. A no-op when no call is
   * up: saving from a call that has since ended must not leave the panel
   * showing a profile for nobody.
   */
  refreshCallerProfile() {
    if (state.caller) void runPreAnswerLookup(state.caller);
  },

  setCfg(patch: Partial<Cfg>) {
    const cfg = { ...state.cfg, ...patch };
    // Same rule for a hand-typed password as for a provisioned one: remembered
    // for this tab, never written to disk. A diagnostic credential for a test
    // endpoint is still a working SIP login on a real switch.
    localStorage.setItem(LS_CFG, JSON.stringify({ ...cfg, pass: '' }));
    set({ cfg });
  },

  setOutHeaders(text: string) {
    localStorage.setItem(LS_HDR, text);
    set({ outHeaders: text });
  },

  applyPreset(id: string) {
    const p = PRESETS.find(x => x.id === id);
    if (!p) return;
    localStorage.setItem(LS_SRC, p.source);
    // Keep any saved password — presets deliberately carry none.
    const cfg = p.cfg ? { ...state.cfg, ...p.cfg } : state.cfg;
    localStorage.setItem(LS_CFG, JSON.stringify(cfg));
    set({ presetId: id, source: p.source, cfg });
  },

  async connect(override?: Cfg) {
    const c = override || state.cfg;
    try {
      set({ phase: 'connecting' });
      log(`connecting ${c.ws} as ${c.user}@${c.domain}`);
      const mgr = new Web.SessionManager(c.ws, {
        aor: `sip:${c.user}@${c.domain}`,
        media: { constraints: { audio: true, video: false }, remote: { audio: getRemoteAudio() } },
        userAgentOptions: {
          authorizationUsername: c.user,
          authorizationPassword: c.pass,
          logLevel: 'warn',
        },
        delegate: {
          onServerConnect: () => log('WS connected'),
          onServerDisconnect: () => { log('WS disconnected'); set({ phase: 'offline' }); },
          onRegistered: () => { log('REGISTERED ✓'); set({ phase: 'registered' }); },
          onUnregistered: () => { log('unregistered'); set({ phase: 'offline' }); },

          onCallCreated: (session: any) => {
            // An outbound consultation leg is the only call we raise while
            // another is already up; anything else is the primary.
            if (primary && primary !== session && state.consultPhase === 'calling') consult = session;
            else if (!primary) primary = session;
          },

          // Ring rather than auto-answer: answering silently opens the agent's
          // microphone with no notice and no way to decline.
          onCallReceived: (session: any) => {
            // A second inbound call while already busy is rejected rather than
            // silently stacked — there is one audio device and one agent.
            if (primary && primary !== session) {
              log('second inbound call rejected — already on a call');
              try { sm?.decline(session); } catch { /* ignore */ }
              return;
            }
            primary = session;
            const who = readCallerId(session);
            const h = readIncomingHeaders(session);
            set({ phase: 'ringing', caller: who.display, inHeaders: h });
            log(`incoming call from ${who.display}`);
            if (Object.keys(h).length) {
              log(`headers: ${Object.entries(h).map(([k, v]) => `${k}=${v}`).join(', ')}`);
            }
            startRing();
            runPreAnswerLookup(who.number);
          },

          onCallAnswered: (session: any) => {
            stopRing();
            if (session === consult) { set({ consultPhase: 'in-call' }); log('consultation answered'); }
            else { set({ phase: 'in-call' }); log('call answered — media flowing'); }
          },

          onCallHangup: (session: any) => {
            stopRing();
            if (session === consult) {
              consult = null;
              set({ consultPhase: 'idle', consultTarget: '' });
              log('consultation ended');
              // The primary is still parked; bring it back so the agent is not
              // left holding a silent call.
              if (primary && state.onHold) actions.unhold();
              return;
            }
            primary = null;
            clearCallContext();
            set({ phase: state.phase === 'offline' ? 'offline' : 'registered' });
            if (pendingConference) {
              const room = pendingConference;
              pendingConference = '';
              log(`joining conference ${room}…`);
              startRoster(room);
              actions.call(room);
            } else {
              // Only now is the agent truly out of the conference.
              if (state.conferenceRoom) { stopRoster(); set({ conferenceRoom: '' }); }
              log('call ended');
            }
          },
        },
      });
      sm = mgr;
      await mgr.connect();
      await mgr.register();
    } catch (e: any) {
      log(`ERROR: ${e?.message || e}`);
      set({ phase: 'offline' });
    }
  },

  /**
   * Load the switches this organization may use.
   *
   * Safe to call repeatedly; it is cheap and carries names and ids only. Failure
   * is deliberately quiet — provisioning still works when the organization has
   * exactly one switch, and a picker that cannot be drawn should not stop a
   * phone that does not need one.
   */
  async loadProviders() {
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.SOFTPHONE_PROVIDERS);
      const d = res.data?.data || res.data;
      const list = Array.isArray(d?.providers) ? d.providers : [];
      set({ providers: list, switchable: !!d?.switchable });
      // Drop a remembered switch the user is no longer entitled to, rather than
      // sending it and being refused. Entitlements change; localStorage does not.
      if (state.providerId && !list.some((p: any) => p.providerId === state.providerId)) {
        localStorage.removeItem(LS_PROVIDER);
        set({ providerId: null });
      }
      // One switch is not a choice. Adopt it silently so the user is never asked
      // a question with a single answer.
      if (list.length === 1) set({ providerId: list[0].providerId });
    } catch { /* the picker is optional; provisioning is not */ }
  },

  /** Choose a switch and re-provision onto it. */
  async selectProvider(providerId: number | null) {
    if (providerId) localStorage.setItem(LS_PROVIDER, String(providerId));
    else localStorage.removeItem(LS_PROVIDER);
    set({ providerId });
    if (state.phase !== 'offline') await actions.disconnect();
    await actions.provisionAndConnect();
  },

  /** Ask the backend for a per-user credential, then connect with it. */
  async provisionAndConnect() {
    set({ provisionError: '' });
    // The list has to be in hand BEFORE provisioning: when an organization has
    // several switches the server refuses to guess, and the request has to carry
    // a choice or it comes back with no credential at all.
    if (!state.providers.length) await actions.loadProviders();
    try {
      log('requesting credential from server…');
      const url = state.providerId
        ? `${API_ENDPOINTS.SOFTPHONE_CREDENTIALS}?providerId=${state.providerId}`
        : API_ENDPOINTS.SOFTPHONE_CREDENTIALS;
      const res = await axiosInstance.get(url);
      const d = res.data?.data || res.data;
      if (d?.username && d?.password) {
        const next: Cfg = {
          ws: d.wsUrl || state.cfg.ws, domain: d.domain || state.cfg.domain,
          user: d.username, pass: d.password,
        };
        // The password stays in MEMORY only. It used to be written to
        // localStorage, where any XSS, any browser extension and anyone on a
        // shared machine could read a live SIP login in plaintext — and it
        // outlived the session that fetched it.
        //
        // Nothing is lost by not persisting it: the credential is fetched again
        // on every connect, so a reload re-provisions rather than reusing what
        // was stored. Note the password is now STABLE rather than re-minted —
        // the endpoint is static config pushed to a switch, so the same value
        // comes back each time and a leaked one stays valid until it is
        // explicitly reissued. Only the non-secret half is remembered, so the
        // form still shows where the phone registers before it has connected.
        localStorage.setItem(LS_CFG, JSON.stringify({ ...next, pass: '' }));
        set({ cfg: next });
        log(`provisioned sip user ${d.username}`);
        await actions.connect(next);
        return;
      }
      // Reached when the server answered but issued nothing. The usual cause is
      // an organization with several switches and no choice made — the server
      // will not guess, because a phone on the wrong switch registers fine and
      // then cannot dial a colleague.
      const why = state.switchable && !state.providerId
        ? 'Your organization has more than one switch. Choose which one this phone should register to.'
        : 'The server did not issue a credential for this account.';
      set({ provisionError: why });
      log(`no credential issued — ${why}`);
      return;
    } catch (e: any) {
      // A refusal is not a reason to fall back to whatever was saved: those
      // settings point at a switch this user may no longer be entitled to, and
      // silently using them turns an authorisation error into a mystery.
      const msg = e?.response?.data?.message
        || e?.response?.data?.error
        || e?.message
        || 'unknown error';
      set({ provisionError: String(msg) });
      log(`credential fetch failed (${e?.response?.status || ''}) — ${msg}`);
      return;
    }
  },

  /**
   * Rotate this phone's SIP password.
   *
   * The credential is stable, so a leaked one stays valid until it is replaced —
   * this is the only way to retire it short of editing the database.
   */
  async reissueCredential() {
    set({ provisionError: '' });
    try {
      if (state.phase !== 'offline') await actions.disconnect();
      const url = state.providerId
        ? `${API_ENDPOINTS.SOFTPHONE_CREDENTIALS_REISSUE}?providerId=${state.providerId}`
        : API_ENDPOINTS.SOFTPHONE_CREDENTIALS_REISSUE;
      await axiosInstance.post(url);
      log('credential reissued — reconnecting');
      await actions.provisionAndConnect();
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'unknown error';
      set({ provisionError: `Could not reissue the credential: ${msg}` });
    }
  },

  async disconnect() {
    try { await sm?.unregister(); await sm?.disconnect(); } catch { /* ignore */ }
    sm = null; primary = null; consult = null; pendingConference = '';
    stopRoster();
    stopRing();
    clearCallContext();
    set({ phase: 'offline', consultPhase: 'idle', consultTarget: '', conferenceRoom: '' });
    log('disconnected');
  },

  async call(dest: string) {
    if (!sm || !dest) return;
    try {
      const target = `sip:${dest}@${state.cfg.domain}`;
      const extra = parseHeaderLines(state.outHeaders);
      log(`calling ${target}${extra.length ? ` with ${extra.length} custom header(s)` : ''}`);
      set({ phase: 'calling' });
      primary = await sm.call(target, extra.length ? { extraHeaders: extra } : undefined);
    } catch (e: any) {
      log(`CALL ERROR: ${e?.message || e}`);
      set({ phase: 'registered' });
    }
  },

  async answer() {
    stopRing();
    if (!sm || !primary) return;
    try { await sm.answer(primary); log('answered'); }
    catch (e: any) { log(`ANSWER ERROR: ${e?.message || e}`); set({ phase: 'registered' }); }
  },

  async decline() {
    stopRing();
    if (sm && primary) { try { await sm.decline(primary); log('declined'); } catch { /* ignore */ } }
    primary = null;
    clearCallContext();
    set({ phase: 'registered' });
  },

  async hangup() {
    if (!sm) return;
    // Drop the consultation first if one is up, so the agent is returned to the
    // parked call rather than losing both at once.
    if (consult) { try { await sm.hangup(consult); } catch { /* ignore */ } return; }
    if (primary) { try { await sm.hangup(primary); } catch { /* ignore */ } }
  },

  async hold() {
    if (!sm || !primary) return;
    try { await sm.hold(primary); set({ onHold: true }); log('call on hold'); }
    catch (e: any) { log(`HOLD FAILED: ${e?.message || e}`); }
  },

  async unhold() {
    if (!sm || !primary) return;
    try { await sm.unhold(primary); set({ onHold: false }); log('call resumed'); }
    catch (e: any) { log(`UNHOLD FAILED: ${e?.message || e}`); }
  },

  /**
   * Blind transfer: hand the far party to `target` and drop out.
   *
   * Sends a SIP REFER, which Asterisk accepts because allow_transfer is on.
   * REFER is an in-dialog request, so this depends on the SBC resolving a
   * WebSocket client's contact alias for in-dialog traffic — which it did not do
   * until recently and would have failed here with 478.
   *
   * Note this does nothing useful if the far end is a media application rather
   * than a party: there is no second leg to hand anywhere.
   */
  async transfer(target: string) {
    if (!primary || !target) return;
    const uri = UserAgent.makeURI(`sip:${target}@${state.cfg.domain}`);
    if (!uri) { log(`transfer: "${target}" is not a valid target`); return; }
    try {
      log(`blind transfer to ${target}…`);
      await primary.refer(uri);
      log('REFER accepted — far party redirected');
    } catch (e: any) {
      log(`TRANSFER FAILED: ${e?.message || e}`);
    }
  },

  /**
   * Attended transfer, step 1: park the caller and ring the target so the agent
   * can introduce the call before connecting anyone.
   *
   * This is why the engine uses SessionManager: two sessions have to be up at
   * once, and SimpleUser handles only one.
   */
  async attendedStart(target: string) {
    if (!sm || !primary || !target) return;
    if (consult) { log('a consultation is already in progress'); return; }
    try {
      await actions.hold();
      set({ consultPhase: 'calling', consultTarget: target });
      log(`consulting ${target}…`);
      consult = await sm.call(`sip:${target}@${state.cfg.domain}`);
    } catch (e: any) {
      log(`CONSULT FAILED: ${e?.message || e}`);
      set({ consultPhase: 'idle', consultTarget: '' });
      consult = null;
      await actions.unhold();
    }
  },

  /**
   * Attended transfer, step 2: connect the two parties to each other.
   *
   * refer() takes the other Session rather than a URI, which is what makes this
   * attended rather than blind — the REFER carries a Replaces header naming the
   * consultation dialog, so the far party joins that established call instead of
   * dialling the target afresh.
   */
  async attendedComplete() {
    if (!primary || !consult) { log('nothing to complete — no consultation up'); return; }
    try {
      log('completing attended transfer…');
      await primary.refer(consult);
      log('transfer completed — both legs handed over');
    } catch (e: any) {
      log(`ATTENDED TRANSFER FAILED: ${e?.message || e}`);
    }
  },

  /** Abandon the consultation and go back to the parked caller. */
  async attendedCancel() {
    if (!sm || !consult) return;
    try { await sm.hangup(consult); } catch { /* ignore */ }
    consult = null;
    set({ consultPhase: 'idle', consultTarget: '' });
    await actions.unhold();
    log('consultation cancelled — back to the caller');
  },

  /**
   * Move the current caller into a conference room and follow them in.
   *
   * Two steps, because there is no single SIP operation for it: blind-transfer
   * the far party to the room, then dial the room ourselves once our own leg has
   * gone. Doing only the first - which is what this button did originally -
   * hands the caller over and hangs up on the agent, which reads as the call
   * dropping.
   */
  async conference(room = '3000') {
    if (!primary) return;
    const uri = UserAgent.makeURI(`sip:${room}@${state.cfg.domain}`);
    if (!uri) { log(`conference: "${room}" is not a valid room`); return; }
    try {
      log(`moving the caller into conference ${room}…`);
      set({ conferenceRoom: room });
      pendingConference = room;
      await primary.refer(uri);
    } catch (e: any) {
      pendingConference = '';
      set({ conferenceRoom: '' });
      log(`CONFERENCE FAILED: ${e?.message || e}`);
    }
  },

  dtmf(d: string) {
    if (!sm) return;
    const s = consult || primary;
    if (!s) return;
    try { sm.sendDTMF(s, d); log(`DTMF ${d}`); } catch { /* ignore */ }
  },
};

/** True when a ws:// endpoint is configured on an https page — the browser
 *  blocks that as mixed content and the only symptom is a socket that never
 *  opens. */
export function isMixedContent(cfg: Cfg): boolean {
  return window.location.protocol === 'https:' && cfg.ws.startsWith('ws://');
}
